import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { PROJECTS } from "../data/content";

const COLOR_HEX = {
  "four-red": "#C85A5A",
  "four-yellow": "#E3B14B",
  "four-green": "#859368",
  "four-blue": "#789BBD",
};

export default function Projects() {
  const { t, isRTL } = useLang();
  const ref = useRef(null);

  useEffect(() => {
    const items = ref.current?.querySelectorAll(".reveal") || [];
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("visible");
        });
      },
      { threshold: 0.12 }
    );
    items.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="projects"
      ref={ref}
      className="relative py-32 lg:py-48 bg-ink-deeper paper-grain"
      data-testid="projects-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="reveal mb-20 max-w-3xl">
          <p className="chapter-num mb-4">{t("the work", "הפעילות")}</p>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-parchment leading-tight mb-4">
            {t("What I'm building.", "מה שאני בונה.")}
          </h2>
          <div className="brush-divider" />
          <p className="text-lg text-parchment-warm/80 font-light leading-relaxed">
            {t(
              "One thesis connects my work: growth is a system, not an accident. Enter any to read the story behind it.",
              "רעיון אחד מחבר את העשייה שלי: צמיחה היא מערכת, לא מקרה. היכנס לכל אחד כדי לקרוא את הסיפור מאחוריו."
            )}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {PROJECTS.filter((p) => !p.hidden).map((p, idx) => (
            <Link
              key={p.slug}
              to={`/projects/${p.slug}`}
              className="reveal project-card group p-8 rounded-sm flex flex-col h-full"
              style={{ animationDelay: `${idx * 80}ms` }}
              data-testid={`project-card-${p.slug}`}
            >
              <div className="flex items-start justify-between mb-6">
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center text-xs font-serif"
                  style={{
                    border: `1.5px solid ${COLOR_HEX[p.color]}`,
                    color: COLOR_HEX[p.color],
                  }}
                  aria-hidden="true"
                >
                  0{idx + 1}
                </div>
                <ArrowUpRight
                  size={20}
                  className="text-parchment-dusk group-hover:text-four-yellow transition-all duration-500 group-hover:-translate-y-1 group-hover:translate-x-1"
                  style={isRTL ? { transform: "scaleX(-1)" } : undefined}
                />
              </div>

              <h3 className="font-serif text-2xl lg:text-3xl font-light text-parchment mb-3 leading-tight">
                {t(p.name.en, p.name.he)}
              </h3>
              <p
                className="text-sm italic font-serif mb-5"
                style={{ color: COLOR_HEX[p.color] }}
              >
                {t(p.tagline.en, p.tagline.he)}
              </p>
              <p className="text-sm text-parchment-warm/80 font-light leading-relaxed flex-grow">
                {t(p.summary.en, p.summary.he)}
              </p>

              <div className="mt-6 pt-5 border-t border-parchment-faint flex items-center text-xs tracking-widest uppercase text-parchment-dusk group-hover:text-parchment transition-colors">
                {t("Read more", "קרא עוד")}
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
